import { zodResolver } from '@hookform/resolvers/zod';
import { INTERVIEW_MODALITY } from '@kiku/interviews/types';
import type { InterviewModality } from '@kiku/interviews/types';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { InterviewFormatFields } from './interview-format-fields';

import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';

const sharedSettingsSchema = z.object({
  employer: z.string().min(1),
  modality: z.array(z.enum(INTERVIEW_MODALITY)).min(1),
  languages: z.array(z.string().length(2)).min(1)
});

type SharedSettings = z.infer<typeof sharedSettingsSchema>;

type SharedSettingsStepProps = {
  defaultValues?: Partial<SharedSettings>;
  onSubmit: (data: SharedSettings) => void;
  onBack: () => void;
  isSubmitting: boolean;
};

export function SharedSettingsStep({
  defaultValues,
  onSubmit,
  onBack,
  isSubmitting
}: SharedSettingsStepProps): React.ReactNode {
  const { t } = useTranslation();
  const {
    watch,
    setValue,
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<SharedSettings>({
    resolver: zodResolver(sharedSettingsSchema),
    defaultValues: {
      employer: '',
      modality: ['text', 'voice'],
      languages: ['en'],
      ...defaultValues
    }
  });

  const selectedLanguages = watch('languages');
  const selectedModalities = watch('modality');
  const employer = watch('employer');

  function toggleModality(m: InterviewModality): void {
    const current = selectedModalities;
    if (current.includes(m)) {
      setValue(
        'modality',
        current.filter((v) => v !== m),
        { shouldValidate: true }
      );
    } else {
      setValue('modality', [...current, m], { shouldValidate: true });
    }
  }

  function toggleLanguage(code: string): void {
    const current = selectedLanguages;
    if (current.includes(code)) {
      setValue(
        'languages',
        current.filter((l: string) => l !== code),
        { shouldValidate: true }
      );
    } else {
      setValue('languages', [...current, code], { shouldValidate: true });
    }
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      className="flex flex-1 min-h-0 flex-col gap-6"
    >
      <div className="flex flex-1 min-h-0 flex-col gap-6 overflow-y-auto">
        <p className="text-sm text-muted-foreground">{t('wizard.sharedSettings.subtitle')}</p>

        <div className="flex flex-col gap-2">
          <label className="text-sm font-medium">{t('wizard.sharedSettings.employer')}</label>
          <Input
            {...register('employer')}
            placeholder={t('wizard.sharedSettings.employerPlaceholder')}
          />
          {errors.employer && <p className="text-sm text-destructive">{errors.employer.message}</p>}
        </div>

        <InterviewFormatFields
          selectedModalities={selectedModalities}
          onModalityToggle={toggleModality}
          modalityError={!!errors.modality}
          selectedLanguages={selectedLanguages}
          onLanguageToggle={toggleLanguage}
          languagesError={!!errors.languages}
        />
      </div>

      <div className="flex justify-between">
        <Button type="button" variant="outline" onClick={onBack}>
          {t('wizard.back')}
        </Button>
        <Button
          type="submit"
          disabled={
            isSubmitting ||
            !employer.trim() ||
            selectedModalities.length === 0 ||
            selectedLanguages.length === 0
          }
        >
          {t('wizard.next')}
        </Button>
      </div>
    </form>
  );
}
